import { h } from "preact";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { createSupportApi } from "./api";
import { TicketForm } from "./components/TicketForm";
import { TicketList } from "./components/TicketList";
import { TicketThread } from "./components/TicketThread";

export function Support({
  apiBase = "/api/support",
  csrfToken = null,
  dealId = null,
}) {
  const api = useMemo(
    () => createSupportApi({ baseUrl: apiBase, csrfToken }),
    [apiBase, csrfToken]
  );

  const [tab, setTab] = useState("tickets");
  const [deals, setDeals] = useState([]);
  const [people, setPeople] = useState([]);
  const [attachmentConfig, setAttachmentConfig] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [bootstrapError, setBootstrapError] = useState("");
  const [createdTicket, setCreatedTicket] = useState(null);

  const loadTickets = useCallback(async (preferredId = null) => {
    setListLoading(true);
    setListError("");

    try {
      const payload = await api.listTickets({ page_size: 50 });
      const nextTickets = payload.data || [];
      setTickets(nextTickets);

      const nextId = preferredId || nextTickets[0]?.id || null;
      setSelectedId(nextId);
      return nextId;
    } catch (error) {
      setListError(error.message);
      return null;
    } finally {
      setListLoading(false);
    }
  }, [api]);

  const loadTicket = useCallback(async (id) => {
    if (!id) {
      setSelectedTicket(null);
      return;
    }

    setDetailLoading(true);
    setDetailError("");

    try {
      const ticket = await api.getTicket(id);
      setSelectedTicket(ticket);

      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("support:unread-changed"));
      }
    } catch (error) {
      setDetailError(error.message);
    } finally {
      setDetailLoading(false);
    }
  }, [api]);

  useEffect(() => {
    let active = true;
    setBootstrapError("");

    Promise.all([api.getConfig(), api.listDeals(), api.listPeople(), loadTickets()])
      .then(([supportConfig, availableDeals, availablePeople, firstTicketId]) => {
        if (!active) return;
        setAttachmentConfig(supportConfig?.attachments || null);
        setDeals(availableDeals);
        setPeople(availablePeople);
        if (firstTicketId) loadTicket(firstTicketId);
      })
      .catch((error) => {
        if (active) setBootstrapError(error.message);
      });

    return () => {
      active = false;
    };
  }, [api, loadTicket, loadTickets]);

  const selectTicket = async (id) => {
    setSelectedId(id);
    await loadTicket(id);
  };

  const createTicket = async (values) => {
    const ticket = await api.createTicket(values);
    setTab("tickets");
    setSelectedId(ticket.id);
    setSelectedTicket(ticket);
    setCreatedTicket(ticket);
    await loadTickets(ticket.id);
  };

  const reply = async (message) => {
    const ticket = await api.reply(selectedId, message);
    setSelectedTicket(ticket);
    await loadTickets(ticket.id);
  };

  return (
    <Box component="section" sx={{ width: "100%", bgcolor: "#fff", p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 1.25, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Tooltip title="Revenir à la page précédente">
              <IconButton
                aria-label="Revenir à la page précédente"
                onClick={() => window.history.back()}
                size="small"
              >
                <Box component="span" aria-hidden="true" sx={{ fontSize: 24, lineHeight: 1 }}>
                  ←
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Typography variant="h5">Mon support</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
        </Box>
      </Paper>

      <Tabs
        value={tab}
        onChange={(_event, nextTab) => setTab(nextTab)}
        aria-label="Navigation du support"
      >
        <Tab value="tickets" label="Mes demandes" />
        <Tab value="new" label="Nouvelle demande" />
      </Tabs>
      <Divider sx={{ mb: 3 }} />

      {bootstrapError ? <Alert severity="error" sx={{ mb: 2 }}>{bootstrapError}</Alert> : null}

      <Dialog
        open={Boolean(createdTicket)}
        onClose={() => setCreatedTicket(null)}
        aria-labelledby="support-created-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="support-created-dialog-title">Demande envoyée</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Votre ticket de support a bien été créé
            {createdTicket?.reference ? ` (${createdTicket.reference})` : ""}.
          </DialogContentText>
          {createdTicket?.attachment_upload_errors?.length ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Le ticket est créé, mais certaines pièces jointes n'ont pas pu être envoyées : {createdTicket.attachment_upload_errors.join(" · ")}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedTicket(null)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {tab === "new" ? (
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Contacter le support
          </Typography>
          <TicketForm
            deals={deals}
            people={people}
            defaultDealId={dealId}
            attachmentConfig={attachmentConfig}
            onSubmit={createTicket}
          />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              <TicketList
                tickets={tickets}
                selectedId={selectedId}
                loading={listLoading}
                error={listError}
                onSelect={selectTicket}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <TicketThread
              key={selectedId || "empty"}
              ticket={selectedTicket}
              loading={detailLoading}
              error={detailError}
              attachmentBaseUrl={apiBase}
              onReply={reply}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
